import type { FC, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

export const CONNECTION_HOVER_ACTION_CLASS = "Connection__HoverAction";

export type ConnectionNavigation = {
  onNavigate: () => void;
  disableNavigation?: boolean;
};

type ConnectionProps = {
  icon: ReactNode;
  name: string;
  endSlot?: ReactNode;
  sx?: SxProps<Theme>;
} & ConnectionNavigation;

export const Connection: FC<ConnectionProps> = ({ icon, name, onNavigate, disableNavigation, endSlot, sx }) => {
  const navigable = !disableNavigation;
  return (
    <Box
      component={navigable ? "button" : "div"}
      type={navigable ? "button" : undefined}
      onClick={navigable ? onNavigate : undefined}
      sx={[
        theme => ({
          all: "unset",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 52,
          px: 2,
          py: 1.5,
          border: "1px solid",
          borderColor: theme.palette.grey["300"],
          borderRadius: 1,
          boxSizing: "border-box",
          ...(navigable
            ? {
                "cursor": "pointer",
                "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
                [`& .${CONNECTION_HOVER_ACTION_CLASS}`]: { visibility: "hidden" },
                [`&:is(:hover, :focus-visible, :focus-within) .${CONNECTION_HOVER_ACTION_CLASS}`]: {
                  visibility: "visible",
                },
                [`&:hover:not(:has(.${CONNECTION_HOVER_ACTION_CLASS}:is(:hover, :focus-visible)))`]: {
                  backgroundColor: theme.palette.action.hover,
                },
              }
            : {}),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {icon}
      <Typography
        sx={theme => ({
          flex: 1,
          minWidth: 0,
          fontSize: theme.typography.pxToRem(14),
          fontWeight: 600,
          color: theme.palette.text.primary,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left",
        })}
      >
        {name}
      </Typography>
      {endSlot}
    </Box>
  );
};
